import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { SizeProp } from "@fortawesome/fontawesome-svg-core";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { alpha } from "@mui/material";
import { IconButtonClose } from "components/iconButtons";
import * as React from "react";
import { Fragment, JSX } from "react";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles<void, 'container' | 'large' | 'medium' | 'iconContainer' | 'iconClose'>()(
    (theme, p, classes) => ({
        root: {
            fontSize: theme.typography.body1.fontSize,
            fontWeight: theme.typography.body1.fontWeight,
            position: "relative",
            backgroundColor: "white",
            color: "#555555",
            borderRadius: "4px",
            width: "75%",
            margin: "0 auto"
        },

        fullWidth: {
            width: "100%",
        },

        container: {
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(2),
        },

        medium: {
            minHeight: "5rem",
        },
        large: {
            minHeight: "8rem",
        },
        messageContainer: {
            textAlign: "left",
        },

        info: {
            backgroundColor: alpha(theme.palette.info.dark, 0.9),
            color: theme.palette.info.contrastText,
            boxShadow: `0 12px 20px -10px ${alpha(theme.palette.info.main, .9)}, 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px ${alpha(theme.palette.info.main, .9)}`,
            [`& .${classes.iconClose}`]: {
                color: theme.palette.info.light
            }
        },
        success: {
            backgroundColor: alpha(theme.palette.success.dark, 0.9),
            color: theme.palette.success.contrastText,
            boxShadow: `0 12px 20px -10px ${alpha(theme.palette.success.main, .9)}, 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px ${alpha(theme.palette.success.main, .9)}`,
            [`& .${classes.iconClose}`]: {
                color: theme.palette.success.light
            }
        },
        warning: {
            backgroundColor: alpha(theme.palette.warning.dark, 0.9),
            color: theme.palette.common.white,
            boxShadow: `0 12px 20px -10px ${alpha(theme.palette.warning.main, .9)}, 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px ${alpha(theme.palette.warning.main, .9)}`,
            [`& .${classes.iconClose}`]: {
                color: theme.palette.warning.light
            }
        },
        error: {
            backgroundColor: alpha(theme.palette.error.dark, 0.9),
            color: theme.palette.common.white,
            boxShadow: `0 12px 20px -10px ${alpha(theme.palette.error.main, .9)}, 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px ${alpha(theme.palette.error.main, .9)}`,
            [`& .${classes.iconClose}`]: {
                color: theme.palette.error.light
            }
        },
        primary: {
            backgroundColor: alpha(theme.palette.primary.dark, 0.9),
            color: theme.palette.primary.contrastText,
            boxShadow: `0 12px 20px -10px ${alpha(theme.palette.primary.main, .9)}, 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px ${alpha(theme.palette.primary.main, .9)}`,
            [`& .${classes.iconContainer}`]: {
                color: theme.palette.primary.light
            },
            [`& .${classes.iconClose}`]: {
                color: theme.palette.primary.light
            }
        },
        secondary: {
            backgroundColor: alpha(theme.palette.secondary.dark, 0.9),
            color: theme.palette.secondary.contrastText,
            boxShadow: `0 12px 20px -10px ${alpha(theme.palette.secondary.main, .9)}, 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px ${alpha(theme.palette.secondary.main, .9)}`,
            [`& .${classes.iconContainer}`]: {
                color: theme.palette.secondary.light
            },
            [`& .${classes.iconClose}`]: {
                color: theme.palette.secondary.light
            }
        },
        notice: {
            backgroundColor: alpha(theme.palette.notice.dark, 0.9),
            color: theme.palette.notice.contrastText,
            boxShadow: `0 12px 20px -10px ${alpha(theme.palette.notice.main, .9)}, 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px ${alpha(theme.palette.notice.main, .9)}`,
            [`& .${classes.iconContainer}`]: {
                color: theme.palette.muted.main
            },
            [`& .${classes.iconClose}`]: {
                color: theme.palette.notice.light
            }
        },
        action: {
            padding: theme.spacing(1),
            textAlign: "center"
        },

        iconContainer: {
            alignSelf: "self-start",
            paddingRight: theme.spacing(2),
            display: "block",
        },

        iconClose: {
            position: "absolute",
            top: 0,
            right: 4
        }
    })
);

interface IPropsMessageBox {
    children?: React.ReactNode;
    message: string | JSX.Element | Array<string | JSX.Element>;
    variant?: "info" | "success" | "warning" | "error" | "primary" | "secondary" | "notice";
    size?: "medium" | "large";
    fullWidth?: boolean,
    icon?: IconDefinition;
    hideIcon?: boolean;
    action?: React.ReactNode | React.ReactNode[];
    onClose?: () => void;
}

export const MprcMessageBox: React.FC<IPropsMessageBox> = (props) => {
    const { action, message, variant, fullWidth, size, hideIcon, icon, children, onClose } = props;
    const { classes, cx } = useStyles();

    let defIcon: Nullable<IconDefinition> = null;
    switch (variant) {
        case "success": defIcon = faCheckCircle; break;
        case "warning": defIcon = faExclamationTriangle; break;
        case "error": defIcon = faExclamationCircle; break;
        case "info": defIcon = faInfoCircle; break;
    }

    let iconSize: SizeProp;
    switch (size) {
        case "large": iconSize = "4x"; break;
        case "medium": iconSize = "3x"; break;
        default: iconSize = "2x";
    }

    const iconToPrint =
        hideIcon !== true && (icon || defIcon)
            ? <FontAwesomeIcon fixedWidth icon={icon! || defIcon!} size={iconSize} />
            : null;

    const m = Array.isArray(message)
        ? (message as Array<any>).reduce(
            (acc: any, v: any) => {
                const item = <Fragment><span>{v}</span><br /></Fragment>;
                acc.push(item);
                return acc;
            }, [])
        : message || children;

    return (
        <div
            className={
                cx(
                    classes.root,
                    classes[variant || "info"],
                    {
                        [classes[size!]]: size != null,
                        [classes.fullWidth]: fullWidth == true
                    }
                )
            }
        >
            <div className={classes.container}>
                {
                    iconToPrint &&
                    <div className={classes.iconContainer}>{iconToPrint}</div>
                }
                <div className={classes.messageContainer}>
                    {
                        onClose &&
                        <div className={classes.iconClose}>
                            <IconButtonClose color="inherit" onClick={onClose} />
                        </div>
                    }

                    {m}
                </div>
            </div>

            {
                action &&
                <div className={classes.action}>
                    {action}
                </div>
            }
        </div >
    );
};
